import { z } from "zod";

/**
 * Electronic Invoice Format Schema
 * Supports XRechnung, ZUGFeRD, and UBL formats for invoices, credit notes, and dunning
 */

// Supported electronic invoice formats
export const EInvoiceFormat = z.enum([
  "UBL",           // Universal Business Language 2.1 - used for Peppol
  "XRECHNUNG",     // German CIUS (Core Invoice Usage Specification) based on EN 16931
  "ZUGFERD",       // German/French hybrid format (PDF/A-3 with embedded CII XML)
  "FACTUR_X",      // French equivalent of ZUGFeRD (same technical spec)
  "CII",           // UN/CEFACT Cross Industry Invoice
]);

export type EInvoiceFormatType = z.infer<typeof EInvoiceFormat>;

// ZUGFeRD/Factur-X profile levels
export const ZUGFeRDProfile = z.enum([
  "MINIMUM",       // Basic info only
  "BASIC_WL",      // Basic without lines
  "BASIC",         // Full basic profile
  "EN16931",       // EN 16931 compliant (Comfort in ZUGFeRD 1.x)
  "EXTENDED",      // Extended profile with additional fields
]);

export type ZUGFeRDProfileType = z.infer<typeof ZUGFeRDProfile>;

// XRechnung versions
export const XRechnungVersion = z.enum([
  "3.0.2",         // Current XRechnung bundle (Winter 2025/26 bugfix)
  "3.0",           // XRechnung 3.0 customization family
  "2.3",           // Previous version
  "2.2",           // Still accepted
]);

export type XRechnungVersionType = z.infer<typeof XRechnungVersion>;

// Document types that support electronic formats
export const EInvoiceDocumentType = z.enum([
  "INVOICE",       // Standard invoice (typeCode 380)
  "CREDIT_NOTE",   // Credit note (typeCode 381)
  "DEBIT_NOTE",    // Debit note (typeCode 383)
  "DUNNING",       // Dunning notice (Mahnung)
]);

export type EInvoiceDocumentTypeType = z.infer<typeof EInvoiceDocumentType>;

// Configuration for electronic invoice generation
export const EInvoiceConfigSchema = z.object({
  // Enabled formats for the tenant
  enabledFormats: z.array(EInvoiceFormat).default(["UBL"]),

  // Default format for different document types
  defaultFormat: z.object({
    invoice: EInvoiceFormat.default("UBL"),
    creditNote: EInvoiceFormat.default("UBL"),
    debitNote: EInvoiceFormat.default("UBL"),
    dunning: EInvoiceFormat.optional(), // Dunning typically doesn't use Peppol
  }).optional(),

  // ZUGFeRD specific settings
  zugferd: z.object({
    profile: ZUGFeRDProfile.default("EN16931"),
    embedInPdf: z.boolean().default(true),
    version: z.enum(["2.1", "2.2", "2.3", "2.4"]).default("2.4"),
  }).optional(),

  // XRechnung specific settings
  xrechnung: z.object({
    version: XRechnungVersion.default("3.0.2"),
    // Leitweg-ID is required for B2G in Germany
    requireLeitwegId: z.boolean().default(false),
    // Routing information
    routingScheme: z.enum(["PEPPOL", "DIRECT"]).default("PEPPOL"),
  }).optional(),

  // UBL/Peppol specific settings
  ubl: z.object({
    customizationId: z.string().default("urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0"),
    profileId: z.string().default("urn:fdc:peppol.eu:2017:poacc:billing:01:1.0"),
  }).optional(),
});

export type EInvoiceConfig = z.infer<typeof EInvoiceConfigSchema>;

// Result of electronic invoice generation
export const EInvoiceResultSchema = z.object({
  format: EInvoiceFormat,
  documentType: EInvoiceDocumentType,
  xml: z.string(),
  xmlFileName: z.string(),

  // For ZUGFeRD: embedded PDF
  pdfWithEmbeddedXml: z.instanceof(Uint8Array).optional(),
  pdfFileName: z.string().optional(),

  // Validation results
  isValid: z.boolean(),
  failureCategory: z.enum(["INPUT_INVALID", "EXECUTION_FAILED"]).optional(),
  validationErrors: z.array(z.string()).optional(),
  validationWarnings: z.array(z.string()).optional(),
  validationEngine: z.string().optional(),
  validationArtifacts: z.array(z.string()).optional(),
  artifactValidated: z.boolean().optional(),

  // Metadata
  generatedAt: z.string().datetime(),
  schemaVersion: z.string().optional(),
  profile: z.string().optional(),
});

export type EInvoiceResult = z.infer<typeof EInvoiceResultSchema>;

// CII (Cross Industry Invoice) namespaces - used by ZUGFeRD/Factur-X and XRechnung CII
export const CII_NAMESPACES = {
  rsm: "urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100",
  ram: "urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100",
  qdt: "urn:un:unece:uncefact:data:standard:QualifiedDataType:100",
  udt: "urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100",
  xsi: "http://www.w3.org/2001/XMLSchema-instance",
} as const;

// UBL namespaces - used by Peppol BIS Billing and XRechnung UBL
export const UBL_NAMESPACES = {
  invoice: "urn:oasis:names:specification:ubl:schema:xsd:Invoice-2",
  creditNote: "urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2",
  cac: "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
  cbc: "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
  ext: "urn:oasis:names:specification:ubl:schema:xsd:CommonExtensionComponents-2",
} as const;

// XRechnung customization IDs
export const XRECHNUNG_CUSTOMIZATION_IDS = {
  UBL_INVOICE: "urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0",
  UBL_CREDIT_NOTE: "urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0",
  CII: "urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0#conformant#urn:xeinkauf.de:kosit:extension:xrechnung_3.0",
} as const;

// ZUGFeRD/Factur-X profile identifiers
export const ZUGFERD_PROFILE_IDS = {
  MINIMUM: "urn:factur-x.eu:1p0:minimum",
  BASIC_WL: "urn:factur-x.eu:1p0:basicwl",
  BASIC: "urn:factur-x.eu:1p0:basic",
  EN16931: "urn:cen.eu:en16931:2017",
  EXTENDED: "urn:factur-x.eu:1p0:extended",
} as const;

// Document type codes (UN/EDIFACT 1001)
export const DOCUMENT_TYPE_CODES = {
  PARTIAL_INVOICE: "326",
  INVOICE: "380",
  CREDIT_NOTE: "381",
  DEBIT_NOTE: "383",
  CORRECTIVE_INVOICE: "384",
  SELF_BILLED_INVOICE: "389",
  PREPAYMENT_INVOICE: "386",
  PARTIAL_CONSTRUCTION_INVOICE: "875",
  PARTIAL_FINAL_CONSTRUCTION_INVOICE: "876",
  FINAL_CONSTRUCTION_INVOICE: "877",
  PROFORMA_INVOICE: "325",
} as const;

// Tax category codes (UNCL 5305)
export const TAX_CATEGORY_CODES = {
  S: "Standard rate",
  Z: "Zero rated goods",
  E: "Exempt from tax",
  AE: "VAT Reverse Charge",
  K: "Intra-community supply",
  G: "Export outside the EU",
  O: "Not subject to VAT",
  L: "Canary Islands general indirect tax",
  M: "Tax for production, services and importation in Ceuta and Melilla",
} as const;

// Payment means codes (UNCL 4461)
export const PAYMENT_MEANS_CODES = {
  CREDIT_TRANSFER: "30",
  SEPA_CREDIT_TRANSFER: "58",
  SEPA_DIRECT_DEBIT: "59",
  DEBIT_CARD: "48",
  CREDIT_CARD: "54",
  CASH: "10",
  CHECK: "20",
  PAYMENT_TO_BANK_ACCOUNT: "42",
} as const;

// Unit codes (UN/ECE Rec 20)
export const UNIT_CODES = {
  PIECE: "H87",
  UNIT: "C62",
  HOUR: "HUR",
  DAY: "DAY",
  WEEK: "WEE",
  MONTH: "MON",
  YEAR: "ANN",
  KILOGRAM: "KGM",
  METER: "MTR",
  SQUARE_METER: "MTK",
  LITER: "LTR",
  SET: "SET",
} as const;

// Validation severity levels
export const EInvoiceValidationSeverity = z.enum([
  "ERROR",        // Must be fixed - document is non-compliant
  "WARNING",      // Should be fixed - may cause issues with recipients
  "INFO",         // Informational - best practice suggestion
]);

export type EInvoiceValidationSeverityType = z.infer<typeof EInvoiceValidationSeverity>;

// Single validation issue
export const EInvoiceValidationIssueSchema = z.object({
  severity: EInvoiceValidationSeverity,
  code: z.string(),            // e.g., "BR-01", "BR-DE-01", "CII-SR-001"
  message: z.string(),
  field: z.string().optional(), // XPath or field name where issue was found
  rule: z.string().optional(),  // Reference to the business rule
});

export type EInvoiceValidationIssue = z.infer<typeof EInvoiceValidationIssueSchema>;

// Overall validation result
export const EInvoiceValidationResultSchema = z.object({
  isValid: z.boolean(),
  format: EInvoiceFormat,
  documentType: EInvoiceDocumentType,
  profile: z.string().optional(),           // e.g., "EN16931", "EXTENDED"
  schemaVersion: z.string().optional(),     // e.g., "3.0.2", "2.3"
  issues: z.array(EInvoiceValidationIssueSchema),
  errorCount: z.number().int().nonnegative(),
  warningCount: z.number().int().nonnegative(),
  validatedAt: z.string(),
  validationEngine: z.string().optional(),
  validationArtifacts: z.array(z.string()).optional(),
  artifactValidated: z.boolean().optional(),
});

export type EInvoiceValidationResult = z.infer<typeof EInvoiceValidationResultSchema>;
